import { molecule, useMolecule } from 'bunshi/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { BookkeepingAsideState } from '~/components/layout/BookkeepingAside';
import { SectionScope } from '~/state/sectionScope';
import { Flow } from '~/types/flow';
import { BookkeepingBankTransaction, BookkeepingTransaction } from '~/types/general-bookkeeping';
import { Transaction } from '~/types/transaction';

const ASIDE_ANIMATION_TIME = 200;

const singleRegistrationAsideMolecule = molecule((_mol, scope) => {
  scope(SectionScope);
  const asideStateAtom = atom<BookkeepingAsideState>(BookkeepingAsideState.UNMOUNTED);
  const transactionIdsAtom = atom<Transaction['id'][]>([]);
  const bookkeepingTransactionIdAtom = atom<BookkeepingTransaction['id'] | null>(null);
  const recommendedReconciliationFlowIdAtom = atom<Flow['id'] | null>(null);

  const openAsideAtom = atom(null, (get, set) => {
    const currentValue = get(asideStateAtom);
    if (currentValue !== BookkeepingAsideState.OPEN) {
      set(asideStateAtom, BookkeepingAsideState.CLOSED);
      setTimeout(() => {
        set(asideStateAtom, BookkeepingAsideState.OPEN);
      });
    }
  });

  // create registration from transaction
  const openAsideWithTransactionAtom = atom(null, (get, set, transaction: BookkeepingBankTransaction) => {
    const transactionIds = transaction.registrationProposal?.transactions.map((t) => t.id) || [transaction.id];

    set(transactionIdsAtom, transactionIds);
    set(recommendedReconciliationFlowIdAtom, transaction.recommendedReconciliationFlow?.id ?? null);
    set(openAsideAtom);
  });

  // edit existing bookkeeping transaction
  const openAsideWithBookkeepingTransactionAtom = atom(
    null,
    (get, set, bookkeepingTransaction: BookkeepingTransaction) => {
      set(bookkeepingTransactionIdAtom, bookkeepingTransaction.id);
      set(
        transactionIdsAtom,
        bookkeepingTransaction.transactions.map((t) => t.id),
      );
      // no recommended reconciliation flow when editing existing bookkeeping transaction
      set(recommendedReconciliationFlowIdAtom, null);
      set(openAsideAtom);
    },
  );

  const closeAsideAtom = atom(null, (get, set, onClose?: () => void) => {
    const currentValue = get(asideStateAtom);
    set(asideStateAtom, BookkeepingAsideState.CLOSED);
    set(transactionIdsAtom, []);
    set(recommendedReconciliationFlowIdAtom, null);
    if (currentValue === BookkeepingAsideState.CLOSED) {
      set(asideStateAtom, BookkeepingAsideState.UNMOUNTED);
      onClose?.();
    } else {
      window.setTimeout(() => {
        set(asideStateAtom, BookkeepingAsideState.UNMOUNTED);
        onClose?.();
      }, ASIDE_ANIMATION_TIME);
    }
  });

  return {
    asideStateAtom,
    transactionIdsAtom,
    bookkeepingTransactionIdAtom,
    recommendedReconciliationFlowIdAtom,
    openAsideAtom,
    openAsideWithTransactionAtom,
    openAsideWithBookkeepingTransactionAtom,
    closeAsideAtom,
  };
});

export const useSingleRegistrationAsideState = () => {
  const { asideStateAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useAtomValue(asideStateAtom);
};

export const useOpenSingleRegistrationAsideWithTransaction = () => {
  const { openAsideWithTransactionAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useSetAtom(openAsideWithTransactionAtom);
};

export const useOpenSingleRegistrationAsideWithBookkeepingTransaction = () => {
  const { openAsideWithBookkeepingTransactionAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useSetAtom(openAsideWithBookkeepingTransactionAtom);
};

export const useTransactionIds = () => {
  const { transactionIdsAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useAtomValue(transactionIdsAtom);
};

export const useBookkeepingTransactionId = () => {
  const { bookkeepingTransactionIdAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useAtomValue(bookkeepingTransactionIdAtom);
};

export const useRecommendedReconciliationFlowId = () => {
  const { recommendedReconciliationFlowIdAtom } = useMolecule(singleRegistrationAsideMolecule);
  return useAtomValue(recommendedReconciliationFlowIdAtom);
};

export const useCloseSingleRegistrationAside = () => {
  const { closeAsideAtom } = useMolecule(singleRegistrationAsideMolecule);
  const closeAside = useSetAtom(closeAsideAtom);
  return useCallback(
    (onClose?: () => void) => {
      closeAside(onClose);
    },
    [closeAside],
  );
};
